import { useState, useEffect, useCallback } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@ubus/shared';

export interface DriverAuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
  role: 'driver';
  driverId: string | null;
  fleetOperatorId: string | null;
  fleetOperatorName: string | null;
}

export function useAuth(): DriverAuthState & { logout: () => Promise<void> } {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [driverId, setDriverId] = useState<string | null>(null);
  const [fleetOperatorId, setFleetOperatorId] = useState<string | null>(null);
  const [fleetOperatorName, setFleetOperatorName] = useState<string | null>(null);

  const fetchDriverProfile = useCallback(async (authUid: string) => {
    const { data, error } = await supabase
      .from('drivers')
      .select('id, fleet_operator_id, fleet_operators(name)')
      .eq('auth_uid', authUid)
      .single();

    if (error || !data) {
      return { driverId: null, fleetOperatorId: null, fleetOperatorName: null };
    }

    const operator = data.fleet_operators as { name?: string } | null;
    return {
      driverId: data.id as string,
      fleetOperatorId: (data.fleet_operator_id as string) ?? null,
      fleetOperatorName: operator?.name ?? null,
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      if (!isMounted) return;

      setSession(s);
      const u = s?.user ?? null;
      setUser(u);

      if (u) {
        const profile = await fetchDriverProfile(u.id);
        if (isMounted) {
          setDriverId(profile.driverId);
          setFleetOperatorId(profile.fleetOperatorId);
          setFleetOperatorName(profile.fleetOperatorName);
        }
      }

      if (isMounted) setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, s) => {
        if (!isMounted) return;

        setSession(s);
        const u = s?.user ?? null;
        setUser(u);

        if (u) {
          const profile = await fetchDriverProfile(u.id);
          if (isMounted) {
            setDriverId(profile.driverId);
            setFleetOperatorId(profile.fleetOperatorId);
            setFleetOperatorName(profile.fleetOperatorName);
          }
        } else {
          setDriverId(null);
          setFleetOperatorId(null);
          setFleetOperatorName(null);
        }

        if (isMounted) setLoading(false);
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchDriverProfile]);

  const logout = useCallback(async (): Promise<void> => {
    await supabase.auth.signOut();
  }, []);

  return {
    session,
    user,
    loading,
    role: 'driver',
    driverId,
    fleetOperatorId,
    fleetOperatorName,
    logout,
  };
}
